import { ageSex, fmtDayShort, fmtMonthDay, fmtTime, groupByDay, initialsOf, zoneName } from '@concord/domain';
import type { CaseDetailView } from '@concord/domain/contracts';
import {
  Button,
  ConfirmDialog,
  Crumbs,
  KeyValue,
  Notice,
  PageHead,
  Panel,
  PanelHead,
  PanelMore,
  PhaseTag,
  Pill,
  useToast,
} from '@concord/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import type { Me } from '../../lib/session';
import { caseKey, useCase } from '../case/CasePage';
import { ConsoleLayout } from '../console/ConsoleLayout';
import s from './panel.module.css';

interface Candidate {
  id: string;
  publicId: number;
  name: string;
  city: string | null;
  timezone: string;
  specialty: string | null;
  focus: string | null;
  inPlan: boolean;
  poolNames: string[];
  openInvitations: string[];
  availability: ('free' | 'conflict')[];
}

interface Candidates {
  rows: Candidate[];
  emptyReason: string | null;
  alreadyOnPanel: string[];
}

/**
 * Building the panel (MAT-01 to MAT-05, MAT-09, MAT-10, MAT-13, MAT-14): manual selection,
 * the only mode; invitations staged until release.
 */
export function BuildPanelPage({ me }: { me: Me }) {
  const { key = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const c = useCase(key);
  const [specialtyId, setSpecialtyId] = useState('');
  const [poolId, setPoolId] = useState('');
  const [q, setQ] = useState('');
  const [inPlanOnly, setInPlanOnly] = useState(true);
  const [onlyFree, setOnlyFree] = useState(false);
  const [override, setOverride] = useState<Candidate | null>(null);
  const [confirmRelease, setConfirmRelease] = useState(false);
  // Steppers change locally at once and save after a short pause, so quick clicks are never lost.
  const [sizes, setSizes] = useState<{ panelSize: number; expectedResponses: number } | null>(null);
  const sizeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const specialties = useQuery({
    queryKey: ['specialties'],
    queryFn: () => api<{ items: { id: string; term: string }[] }>('/catalog/specialties'),
  });
  const pools = useQuery({
    queryKey: ['case-pools', key],
    queryFn: () =>
      api<{ items: { id: string; name: string; members: number; matchesCase: boolean }[] }>(
        `/staff/cases/${key}/pools`,
      ),
    enabled: c.isSuccess,
  });
  const pool = pools.data?.items.find((x) => x.id === poolId);
  const candidates = useQuery({
    queryKey: ['candidates', key, poolId, specialtyId, q, inPlanOnly, onlyFree],
    queryFn: () =>
      api<Candidates>(
        `/staff/cases/${key}/candidates?${new URLSearchParams({
          ...(specialtyId ? { specialtyId } : {}),
          ...(poolId ? { poolId } : {}),
          q,
          outOfPlan: String(!inPlanOnly),
          onlyFree: String(onlyFree),
        })}`,
      ),
    enabled: c.isSuccess,
  });
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: caseKey(key) }),
      qc.invalidateQueries({ queryKey: ['candidates', key] }),
    ]);

  const stage = useMutation({
    mutationFn: (v: { expertId: string; override: boolean }) => api(`/staff/cases/${key}/panel`, { body: v }),
    onSuccess: refresh,
    onError: (e) => toast(errorMessage(e), 'bad'),
  });
  const unstage = useMutation({
    mutationFn: (id: string) =>
      api(`/staff/cases/${key}/invitations/${id}/withdraw`, { body: { reason: null } }),
    onSuccess: refresh,
  });
  const size = useMutation({
    mutationFn: (v: { panelSize: number; expectedResponses: number }) =>
      api(`/staff/cases/${key}/panel`, { method: 'PATCH', body: v }),
    onSuccess: refresh,
    onError: (e) => toast(errorMessage(e), 'bad'),
  });
  const release = useMutation({
    mutationFn: () =>
      c.data?.state === 'submitted'
        ? api<{ issued: number }>(`/staff/cases/${key}/release`, { body: {} })
        : api<{ issued: number }>(`/staff/cases/${key}/invitations/send`, { body: {} }),
    onSuccess: async (r) => {
      await refresh();
      toast(`${r.issued} ${r.issued === 1 ? 'invitation' : 'invitations'} sent.`);
      navigate(`/console/cases/${key}`);
    },
  });

  if (!c.data) {
    return (
      <ConsoleLayout me={me} current="cases">
        {c.isError ? (
          <Notice tone="bad">{errorMessage(c.error)}</Notice>
        ) : (
          <div className="skeleton" style={{ height: 420 }} />
        )}
      </ConsoleLayout>
    );
  }
  const k = c.data;
  const current = sizes ?? { panelSize: k.panelSize, expectedResponses: k.expectedResponses };
  const changeSize = (next: { panelSize: number; expectedResponses: number }) => {
    setSizes(next);
    if (sizeTimer.current) clearTimeout(sizeTimer.current);
    sizeTimer.current = setTimeout(() => size.mutate(next, { onSettled: () => setSizes(null) }), 350);
  };
  const tz = me.timezone;
  const staged = k.invitations.filter((i) => i.state === 'staged');
  const live = k.invitations.filter((i) => i.state !== 'staged');
  const accepted = live.filter((i) => i.state === 'accepted').length;
  const stillNeeded = Math.max(
    0,
    k.panelSize - staged.length - live.filter((i) => ['invited', 'accepted'].includes(i.state)).length,
  );
  const released = k.state !== 'submitted' && k.state !== 'on_hold';
  const times = groupByDay(k.times, (t) => new Date(t.startsAt), tz);
  const range = k.times.length
    ? `${k.times.length} proposed times, ${dateRange(new Date(k.times[0]!.startsAt), new Date(k.times.at(-1)!.startsAt), tz)}`
    : 'no proposed times';
  const blockers = k.blockers.filter((b) => b.code !== 'no_panel' || staged.length === 0);
  const canSend = staged.length > 0 && (released ? true : blockers.length === 0);
  const rows = candidates.data?.rows ?? [];

  return (
    <ConsoleLayout me={me} current="cases">
      <Crumbs
        items={[
          { label: 'Cases', to: '/console/cases' },
          { label: k.stateLabel, to: '/console/cases' },
          { label: k.reference ?? '', to: `/console/cases/${key}`, isRef: true },
          { label: 'Build the panel' },
        ]}
      />
      <PageHead
        title={
          <>
            Build the panel{' '}
            <Pill tone={released ? 'wait' : 'hold'} className="state">
              {released ? k.stateLabel : 'Not yet released'}
            </Pill>
          </>
        }
        when={
          <>
            <span className="ref">{k.reference}</span> · {k.patient.firstName} {k.patient.lastName} ·{' '}
            {k.caseType?.term} · {ageSex(k.demographics.age, k.demographics.gender) || 'age not given'} ·{' '}
            {k.entitlement.customerName} › {k.entitlement.planName} › {k.entitlement.poolName} network ·{' '}
            {range}
          </>
        }
        actions={
          <Button variant="sec" onClick={() => navigate(`/console/cases/${key}`)}>
            Back to case
          </Button>
        }
      />

      <div className={s.layout}>
        <div className={s.col}>
          <Panel aria-label="Candidate physicians">
            <PanelHead
              title="Candidates"
              sub="Manual selection · the only mode"
              more={
                <PanelMore onClick={() => navigate('/console/directory?q=')}>Open the directory →</PanelMore>
              }
            />
            <div className={s.filters}>
              <div className="field">
                <label htmlFor="pool">Physician pool</label>
                <select
                  className="select"
                  id="pool"
                  value={poolId}
                  onChange={(e) => setPoolId(e.target.value)}
                >
                  <option value="">No pool (by case type)</option>
                  {pools.data?.items.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name} ({x.members}){x.matchesCase ? ' · for this cancer type' : ''}
                    </option>
                  ))}
                </select>
                <span className="hint">
                  {pool
                    ? `Choosing from the ${pool.name} pool.`
                    : `Narrows to physicians associated with ${k.caseType?.term ?? 'the case type'}.`}
                </span>
              </div>
              <div className="field">
                <label htmlFor="sp">Specialty</label>
                <select
                  className="select"
                  id="sp"
                  value={specialtyId}
                  onChange={(e) => setSpecialtyId(e.target.value)}
                >
                  <option value="">Any specialty</option>
                  {specialties.data?.items.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.term}
                    </option>
                  ))}
                </select>
                <span className="hint">Independent of case type. The two catalogs are separate.</span>
              </div>
              <div className="field">
                <label htmlFor="q">Find by name</label>
                <input
                  className="input"
                  id="q"
                  placeholder="Type a physician's name"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                <span className="hint">Searches within the filtered list.</span>
              </div>
            </div>
            <div className={s.chips}>
              Showing
              <span className={`${s.chip} ${s.on}`}>{pool ? `${pool.name} pool` : k.caseType?.term}</span>
              <button
                type="button"
                className={`${s.chip} ${inPlanOnly ? s.on : ''}`}
                aria-pressed={inPlanOnly}
                onClick={() => setInPlanOnly(true)}
              >
                In the plan's network
              </button>
              <button
                type="button"
                className={`${s.chip} ${onlyFree ? s.on : ''}`}
                aria-pressed={onlyFree}
                onClick={() => setOnlyFree((v) => !v)}
              >
                Free in the proposed window
              </button>
              <button
                type="button"
                className={`${s.chip} ${!inPlanOnly ? s.on : ''}`}
                aria-pressed={!inPlanOnly}
                onClick={() => setInPlanOnly(false)}
              >
                Out of plan
              </button>
              <span className={s.clear}>
                {rows.length} {rows.length === 1 ? 'candidate' : 'candidates'}
              </span>
            </div>
            <div className="tw">
              <table className={s.cand}>
                <thead>
                  <tr>
                    <th>Physician</th>
                    <th>Specialty</th>
                    <th>Plan &amp; network</th>
                    <th>Open invitations</th>
                    <th>Free in window</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const free = r.availability.filter((a) => a === 'free').length;
                    return (
                      <tr key={r.id}>
                        <td>
                          <div className={s.nm}>
                            {r.name}
                            <span className={s.id}>ID {r.publicId}</span>
                          </div>
                          <div className="sub2">
                            {r.city ?? '—'} · {zoneName(r.timezone)}
                          </div>
                        </td>
                        <td className={s.sp}>
                          {r.specialty}
                          {r.focus && <div className="sub2">{r.focus}</div>}
                        </td>
                        <td>
                          <span className={`${s.plan} ${r.inPlan ? s.in : s.out}`}>
                            {r.inPlan ? 'In plan' : 'Out of plan'}
                          </span>
                          {!r.inPlan && r.poolNames.length > 0 && (
                            <div className="sub2">{r.poolNames.join(', ')} network</div>
                          )}
                        </td>
                        <td>
                          {r.openInvitations.length}
                          {r.openInvitations.length > 0 && (
                            <div className="sub2">{r.openInvitations.join(', ')}</div>
                          )}
                        </td>
                        <td>
                          <div className={s.avail} title="Free at each offered time">
                            {r.availability.map((a, i) => (
                              // biome-ignore lint/suspicious/noArrayIndexKey: one dot per offered time, in order
                              <i key={i} className={a === 'conflict' ? s.conf : undefined} />
                            ))}
                            <span className={s.c}>
                              {free} of {r.availability.length}
                            </span>
                          </div>
                        </td>
                        <td>
                          {r.inPlan ? (
                            <button
                              type="button"
                              className={s.add}
                              onClick={() => stage.mutate({ expertId: r.id, override: false })}
                            >
                              + Add
                            </button>
                          ) : (
                            <button
                              type="button"
                              className={`${s.add} ${s.warn}`}
                              onClick={() => setOverride(r)}
                            >
                              Override
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className={s['hidden-note']}>
              {candidates.data?.alreadyOnPanel.length ? (
                <>
                  <b>{candidates.data.alreadyOnPanel.length} not shown:</b>{' '}
                  {candidates.data.alreadyOnPanel.join(' and ')}{' '}
                  {candidates.data.alreadyOnPanel.length === 1 ? 'is' : 'are'} already on this panel.
                </>
              ) : (
                <span>Everyone on the panel is excluded from this list.</span>
              )}
              {candidates.data?.emptyReason && (
                <span style={{ marginLeft: 'auto' }}>{candidates.data.emptyReason}</span>
              )}
            </div>
          </Panel>

          <div className={s.emptybox}>
            <span className={s.k}>Facts each row carries in Phase 1</span>
            <span>
              Specialty, plan and network status, invitations currently open on other cases, and whether the
              physician is free during the times the patient offered. Prior consultations on this case type,
              acceptance rate and response time arrive with selection intelligence <PhaseTag phase="P5" />.
            </span>
          </div>
        </div>

        <div className={s.col}>
          <Panel aria-label="Panel being assembled">
            <PanelHead
              title={`Panel for ${k.reference}`}
              sub={`${staged.length} staged${live.length ? ` · ${live.length} invited` : ''}`}
            />
            <div className={s.sizectl}>
              <Stepper
                label="Panel size"
                value={current.panelSize}
                hint="Plan default 3. Adjustable before and after invitations issue."
                onChange={(v) =>
                  changeSize({ panelSize: v, expectedResponses: Math.max(v, current.expectedResponses) })
                }
                min={1}
              />
              <Stepper
                label="Expected responses"
                value={current.expectedResponses}
                hint="Invite above panel size to cover non-response."
                onChange={(v) => changeSize({ panelSize: current.panelSize, expectedResponses: v })}
                min={current.panelSize}
              />
            </div>
            <div className={s.staged}>
              {[...live, ...staged].map((i) => (
                <div key={i.id} className={s.srow}>
                  <span className={s.av}>{initialsOf(i.expertName)}</span>
                  <div className={s.b}>
                    <div className={s.n1}>{i.expertName}</div>
                    <div className={s.n2}>
                      {i.specialty} · {i.outOfPlan ? 'Out of plan' : 'In plan'} ·{' '}
                      {i.state === 'staged'
                        ? `staged ${fmtTime(new Date(i.stagedAt), tz)}`
                        : i.state.replace('_', ' ')}
                    </div>
                  </div>
                  {i.state === 'staged' ? (
                    <button type="button" className={s.rm} onClick={() => unstage.mutate(i.id)}>
                      Remove
                    </button>
                  ) : (
                    <span className={s.st}>{i.state === 'accepted' ? 'Accepted' : 'Invited'}</span>
                  )}
                </div>
              ))}
              {stillNeeded > 0 && (
                <div className={`${s.srow} ${s.empty}`}>
                  {stillNeeded} more {stillNeeded === 1 ? 'fills' : 'fill'} the panel. You can release now and
                  add later.
                </div>
              )}
            </div>
            <div className={s.release}>
              {!released && blockers.length === 0 ? (
                <div className={s['hold-why']}>
                  <span className={s.g} />
                  <span>
                    Ready to release: the patient offered {k.times.length}{' '}
                    {k.times.length === 1 ? 'time' : 'times'} and the entitlement chain is valid.
                  </span>
                </div>
              ) : !released ? (
                <div className={`${s['hold-why']} ${s.blocked}`}>
                  <span className={s.g} />
                  <span>{blockers.map((b) => b.message).join(' ')}</span>
                </div>
              ) : null}
              <div className={s.what}>
                {released ? 'Sending' : 'Release'} will{' '}
                <b>
                  issue {staged.length} {staged.length === 1 ? 'invitation' : 'invitations'}
                </b>{' '}
                by secure email link, each carrying the case, the {k.attachments.length}{' '}
                {k.attachments.length === 1 ? 'record' : 'records'} and the {k.times.length} proposed times.
                They <b>expire 24 hours after issue</b>. Nobody is contacted until you{' '}
                {released ? 'send' : 'release'}.
              </div>
              {release.isError && <Notice tone="bad">{errorMessage(release.error)}</Notice>}
              <Button size="lg" disabled={!canSend} onClick={() => setConfirmRelease(true)}>
                {released
                  ? `Send ${staged.length} ${staged.length === 1 ? 'invitation' : 'invitations'}`
                  : `Release case · send ${staged.length} ${staged.length === 1 ? 'invitation' : 'invitations'}`}
              </Button>
              <span className="sub2">
                Status on each staged physician reads <em>not yet issued</em> until then.
                {accepted > 0 && ` ${accepted} accepted so far.`}
              </span>
            </div>
          </Panel>

          <Panel aria-label="Times the patient offered">
            <PanelHead
              title={`Times ${k.patient.firstName} offered`}
              sub={`${k.times.length} · ${zoneName(tz).replace(' Time', '')}`}
            />
            <div className="pbody" style={{ paddingTop: '.7rem' }}>
              <KeyValue
                rows={times.map((g) => [
                  fmtDayShort(new Date(g.items[0]!.startsAt), tz),
                  g.items.map((t) => fmtTime(new Date(t.startsAt), tz)).join(' · '),
                ])}
              />
            </div>
          </Panel>
        </div>
      </div>

      <ConfirmDialog
        open={override !== null}
        onOpenChange={(v) => !v && setOverride(null)}
        title={`Add ${override?.name} out of plan?`}
        consequence={`${override?.name} is outside the ${k.entitlement.poolName} network on this case's plan. Adding them is an out-of-plan override, recorded on the case for staff.`}
        confirmLabel="Add with override"
        busy={stage.isPending}
        onConfirm={() => {
          if (override)
            stage.mutate({ expertId: override.id, override: true }, { onSettled: () => setOverride(null) });
        }}
      />
      <ConfirmDialog
        open={confirmRelease}
        onOpenChange={setConfirmRelease}
        title={
          released
            ? `Send ${staged.length} more ${staged.length === 1 ? 'invitation' : 'invitations'}?`
            : `Release ${k.reference}?`
        }
        consequence={
          released
            ? `The physicians you staged will be emailed their secure links now. This cannot be undone.`
            : `The physicians you staged will be emailed their secure links now, and the ${k.attachments.length} records lock: they can no longer change. This cannot be undone.`
        }
        confirmLabel={released ? 'Send invitations' : 'Release and send'}
        busy={release.isPending}
        onConfirm={() => release.mutate(undefined, { onSettled: () => setConfirmRelease(false) })}
      />
    </ConsoleLayout>
  );
}

function Stepper({
  label,
  value,
  hint,
  onChange,
  min,
}: {
  label: string;
  value: number;
  hint: string;
  onChange: (v: number) => void;
  min: number;
}) {
  return (
    <div className="field">
      <span className="lbl">{label}</span>
      <div className={s.stepper}>
        <button
          type="button"
          aria-label={`Decrease ${label.toLowerCase()}`}
          disabled={value <= min}
          onClick={() => onChange(value - 1)}
        >
          −
        </button>
        <span className={s.v} aria-live="polite">
          {value}
        </span>
        <button
          type="button"
          aria-label={`Increase ${label.toLowerCase()}`}
          disabled={value >= 10}
          onClick={() => onChange(value + 1)}
        >
          +
        </button>
      </div>
      <span className="hint">{hint}</span>
    </div>
  );
}

export type { CaseDetailView };

/** "Aug 11 – 14", or "Sep 30 – Oct 3" across months. */
function dateRange(a: Date, b: Date, tz: string): string {
  const first = fmtMonthDay(a, tz);
  const last = fmtMonthDay(b, tz);
  if (first === last) return first;
  return first.split(' ')[0] === last.split(' ')[0]
    ? `${first} – ${last.split(' ')[1]}`
    : `${first} – ${last}`;
}
